import { useEffect, useState } from "react";
import { Lock, ShieldCheck, Fingerprint, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useSecurePrivacy } from "@/contexts/SecurePrivacyContext";
import { isPlatformAuthenticatorAvailable, unlockBiometric } from "@/lib/webauthn";

/**
 * PrivacyShield — full-screen overlay while the app is "locked".
 *
 * Unlock priority (best-first):
 *   1. Platform biometric (Face ID / Touch ID / Windows Hello) — if the
 *      user has previously registered a passkey on this device.
 *   2. Password re-verification — only when running as an installed
 *      PWA AND `require_reauth` is on.
 *   3. Plain "Return to VAULT" click — the honest web-deterrent fallback.
 */
export default function PrivacyShield() {
    const {
        shieldActive, unlock, require_reauth, standalone, verifyPassword,
    } = useSecurePrivacy();
    const [bioAvailable, setBioAvailable] = useState(false);
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!shieldActive) return;
        isPlatformAuthenticatorAvailable().then(setBioAvailable);
    }, [shieldActive]);

    const needsPassword = shieldActive && require_reauth && standalone && !bioAvailable;

    if (!shieldActive) return null;

    const tryBiometric = async () => {
        setBusy(true); setError("");
        const ok = await unlockBiometric();
        setBusy(false);
        if (ok) {
            unlock();
            toast.success("Unlocked with biometric.");
        } else {
            setError("Biometric cancelled or unavailable. Try again or use password.");
        }
    };

    const submitPassword = async (e) => {
        e?.preventDefault?.();
        if (!password) { setError("Please enter your password."); return; }
        setBusy(true); setError("");
        const ok = await verifyPassword(password);
        setBusy(false);
        if (ok) { setPassword(""); unlock(); toast.success("Welcome back."); }
        else { setError("Incorrect password. Please try again."); }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="VAULT Privacy Shield"
            data-testid="privacy-shield"
            style={{
                position: "fixed", inset: 0, zIndex: 2147483000,
                background: "linear-gradient(180deg,#0B1122 0%,#05080F 100%)",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                color: "#F6F3EC", textAlign: "center", padding: "24px",
                userSelect: "none",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
                <ShieldCheck size={22} style={{ color: "#C6A86B" }} />
                <div style={{ letterSpacing: "0.28em", fontWeight: 900, fontSize: 22 }}>VAULT</div>
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", maxWidth: 520 }}>
                Financial information protected
            </h1>
            <p style={{ marginTop: 14, color: "rgba(246,243,236,0.7)", maxWidth: 480, fontSize: 15, lineHeight: 1.55 }}>
                Screen Privacy is active. {bioAvailable
                    ? "Use your device biometric to continue."
                    : needsPassword
                        ? "Please verify your identity before we reveal financial data."
                        : "Return to VAULT to continue where you left off."}
            </p>

            {bioAvailable ? (
                <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
                    <button
                        onClick={tryBiometric}
                        disabled={busy}
                        data-testid="privacy-shield-biometric"
                        style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            background: "#C6A86B", color: "#0B1122", padding: "12px 22px",
                            borderRadius: 999, border: "none", fontWeight: 700, fontSize: 14,
                            cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
                        }}
                    >
                        <Fingerprint size={14} /> {busy ? "Verifying…" : "Unlock with biometric"}
                    </button>
                    <button
                        onClick={unlock}
                        data-testid="privacy-shield-unlock"
                        style={{ background: "none", border: "none", color: "rgba(246,243,236,0.55)", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}
                    >
                        Continue without biometric
                    </button>
                </div>
            ) : needsPassword ? (
                <form onSubmit={submitPassword} style={{ marginTop: 24, width: "min(360px, 90vw)" }} data-testid="privacy-shield-reauth-form">
                    <input
                        type="password" autoFocus placeholder="Password"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        data-testid="privacy-shield-reauth-password"
                        style={{
                            width: "100%", padding: "12px 16px", borderRadius: 10,
                            background: "rgba(246,243,236,0.04)", color: "#F6F3EC",
                            border: "1px solid rgba(246,243,236,0.12)",
                            outline: "none", fontSize: 15,
                        }}
                    />
                    <button
                        type="submit" disabled={busy}
                        data-testid="privacy-shield-reauth-submit"
                        style={{
                            marginTop: 14, width: "100%",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                            background: "#C6A86B", color: "#0B1122", padding: "12px 22px",
                            borderRadius: 999, border: "none", fontWeight: 700, fontSize: 14,
                            cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
                        }}
                    >
                        <KeyRound size={14} /> {busy ? "Verifying…" : "Unlock VAULT"}
                    </button>
                </form>
            ) : (
                <button
                    onClick={unlock}
                    data-testid="privacy-shield-unlock"
                    style={{
                        marginTop: 28, display: "inline-flex", alignItems: "center", gap: 8,
                        background: "#C6A86B", color: "#0B1122", padding: "12px 22px",
                        borderRadius: 999, border: "none", fontWeight: 700, fontSize: 14,
                        cursor: "pointer",
                    }}
                >
                    <Lock size={14} /> Return to VAULT
                </button>
            )}

            {error && <div style={{ marginTop: 14, color: "#F87171", fontSize: 13 }} data-testid="privacy-shield-error">{error}</div>}

            <div style={{ marginTop: 20, fontSize: 11, letterSpacing: "0.14em", color: "rgba(246,243,236,0.4)", textTransform: "uppercase" }}>
                Screen Privacy Protection · {bioAvailable ? "Biometric ready" : standalone ? "PWA · Reauth ready" : "Web deterrence"}
            </div>
        </div>
    );
}
